import { getDb } from './db';
import { txKey, markDuplicates } from './dedup';
import type { Category } from '@/app/format';
import { SEED_MONTH, FACTORY_SEED } from '@/app/seed';

export interface Expense {
  id: string;
  ym: string;
  name: string;
  category: Category;
  amount: number;
  notes: string;
  paid: boolean;
  paidAt: string | null;
  source: string | null;
}

export interface Month {
  ym: string;
  label: string;
  created: string;
}

function uid() {
  return (
    (globalThis.crypto && 'randomUUID' in globalThis.crypto && (globalThis.crypto as any).randomUUID()) ||
    'id-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9)
  );
}

/* ---------- meta ---------- */
export async function metaGet(key: string): Promise<string | null> {
  const db = await getDb();
  const r = db.exec({ sql: 'SELECT value FROM meta WHERE key=?', bind: [key], returnValue: 'resultRows' });
  return r && r[0] ? r[0][0] : null;
}
export async function metaSet(key: string, value: string) {
  const db = await getDb();
  db.exec({
    sql: 'INSERT INTO meta(key,value) VALUES(?,?) ON CONFLICT(key) DO UPDATE SET value=excluded.value',
    bind: [key, value],
  });
}
export async function metaGetAll(): Promise<Record<string, string>> {
  const db = await getDb();
  const r = db.exec('SELECT key,value FROM meta', { returnValue: 'resultRows' }) as string[][];
  const out: Record<string, string> = {};
  for (const [k, v] of r) out[k] = v;
  return out;
}

/* ---------- months ---------- */
export async function ensureMonth(ym: string, label: string) {
  const db = await getDb();
  db.exec({
    sql: 'INSERT OR IGNORE INTO months(ym,label,created) VALUES(?,?,?)',
    bind: [ym, label, new Date().toISOString()],
  });
}

export async function listMonths(): Promise<Month[]> {
  const db = await getDb();
  const r = db.exec('SELECT ym,label,created FROM months ORDER BY ym', { returnValue: 'resultRows' }) as string[][];
  return r.map(([ym, label, created]) => ({ ym, label, created }));
}

/* ---------- expenses ---------- */
function rowToExpense(row: any[]): Expense {
  return {
    id: row[0],
    ym: row[1],
    name: row[2],
    category: row[3] as Category,
    amount: row[4],
    notes: row[5] || '',
    paid: !!row[6],
    paidAt: row[7] || null,
    source: row[8] || null,
  };
}

export async function listExpenses(ym: string): Promise<Expense[]> {
  const db = await getDb();
  const r = db.exec({
    sql: 'SELECT id,ym,name,category,amount,notes,paid,paid_at,source FROM expenses WHERE ym=? ORDER BY rowid',
    bind: [ym],
    returnValue: 'resultRows',
  }) as any[][];
  return r.map(rowToExpense);
}

export async function addExpense(
  e: Omit<Expense, 'id' | 'paid' | 'paidAt' | 'source'> & { source?: string },
): Promise<Expense> {
  const db = await getDb();
  const id = uid();
  db.exec({
    sql: 'INSERT INTO expenses(id,ym,name,category,amount,notes,paid,paid_at,source) VALUES(?,?,?,?,?,?,0,NULL,?)',
    bind: [id, e.ym, e.name, e.category, e.amount, e.notes, e.source || 'manual'],
  });
  return { id, ...e, paid: false, paidAt: null, source: e.source || 'manual' };
}

export async function addExpensesBulk(
  items: (Omit<Expense, 'id' | 'paid' | 'paidAt' | 'source'> & { source?: string })[],
): Promise<void> {
  const db = await getDb();
  for (const e of items) {
    db.exec({
      sql: 'INSERT INTO expenses(id,ym,name,category,amount,notes,paid,paid_at,source) VALUES(?,?,?,?,?,?,0,NULL,?)',
      bind: [uid(), e.ym, e.name, e.category, e.amount, e.notes, e.source || 'manual'],
    });
  }
}

export async function togglePaid(id: string) {
  const db = await getDb();
  const r = db.exec({
    sql: 'SELECT paid FROM expenses WHERE id=?',
    bind: [id],
    returnValue: 'resultRows',
  }) as any[][];
  if (!r.length) return;
  const nowPaid = !r[0][0];
  db.exec({
    sql: 'UPDATE expenses SET paid=?, paid_at=? WHERE id=?',
    bind: [nowPaid ? 1 : 0, nowPaid ? new Date().toISOString() : null, id],
  });
}

export async function deleteExpense(id: string) {
  const db = await getDb();
  db.exec({ sql: 'DELETE FROM expenses WHERE id=?', bind: [id] });
}

export async function resetMonthPaid(ym: string) {
  const db = await getDb();
  db.exec({
    sql: 'UPDATE expenses SET paid=0, paid_at=NULL WHERE ym=?',
    bind: [ym],
  });
}

export async function factoryReset() {
  const db = await getDb();
  db.exec('DELETE FROM expenses');
  db.exec('DELETE FROM months');
  db.exec('DELETE FROM transactions');
  for (const k of [
    'v1_imported_at', 'onboarded', 'currentMonth', 'income', 'amountsHidden', 'filter',
  ]) {
    db.exec({ sql: 'DELETE FROM meta WHERE key=?', bind: [k] });
  }
}

/* ---------- transactions (raw statement rows) ---------- */
export interface RawTransaction {
  id: string;
  bank: string;
  account: string | null;
  postedAt: string;
  description: string;
  amountCents: number;
  balanceCents: number | null;
  category: string | null;
  importedAt: string;
  sourceFile: string | null;
}

export interface ImportOutcome {
  inserted: number;
  duplicates: number;
}

/**
 * Insert statement rows, skipping duplicates. A row is a duplicate when a
 * transaction with the same bank + date + description + amount already
 * exists (re-uploaded file or an overlapping statement period). Genuine
 * repeats beyond the stored occurrence count are kept — see dedup.ts.
 */
export async function addTransactionsBulk(txs: Omit<RawTransaction, 'id' | 'importedAt'>[]): Promise<ImportOutcome> {
  const db = await getDb();
  const importedAt = new Date().toISOString();

  // Existing occurrence counts, in one query
  const rows = db.exec(
    `SELECT bank, posted_at, description, amount_cents, COUNT(*)
     FROM transactions
     GROUP BY bank, posted_at, description, amount_cents`,
    { returnValue: 'resultRows' },
  ) as any[][];
  const existing = new Map<string, number>();
  for (const [bank, posted, desc, cents, n] of rows) {
    existing.set(txKey(bank, posted, desc, Number(cents)), Number(n));
  }

  const keys = txs.map(t => txKey(t.bank, t.postedAt, t.description, t.amountCents));
  const isDup = markDuplicates(keys, existing);

  let inserted = 0;
  let duplicates = 0;
  for (let i = 0; i < txs.length; i++) {
    if (isDup[i]) { duplicates++; continue; }
    const t = txs[i];
    db.exec({
      sql: `INSERT INTO transactions(id,bank,account,posted_at,description,amount_cents,balance_cents,category,imported_at,source_file)
            VALUES(?,?,?,?,?,?,?,?,?,?)`,
      bind: [
        uid(), t.bank, t.account, t.postedAt, t.description,
        t.amountCents, t.balanceCents, t.category, importedAt, t.sourceFile,
      ],
    });
    inserted++;
  }
  return { inserted, duplicates };
}

export async function listTransactions(): Promise<RawTransaction[]> {
  const db = await getDb();
  const r = db.exec(
    `SELECT id,bank,account,posted_at,description,amount_cents,balance_cents,category,imported_at,source_file
     FROM transactions ORDER BY posted_at DESC`,
    { returnValue: 'resultRows' },
  ) as any[][];
  return r.map(row => ({
    id: row[0],
    bank: row[1],
    account: row[2],
    postedAt: row[3],
    description: row[4],
    amountCents: row[5],
    balanceCents: row[6],
    category: row[7],
    importedAt: row[8],
    sourceFile: row[9],
  }));
}

/* ---------- uploaded statements (management) ---------- */
export interface UploadSummary {
  sourceFile: string;   // display label; '(unknown file)' when null
  banks: string[];      // distinct banks contributing to this file
  count: number;
  firstPosted: string | null;
  lastPosted: string | null;
  importedAt: string;
}

export async function listUploads(): Promise<UploadSummary[]> {
  const db = await getDb();
  const r = db.exec(
    `SELECT COALESCE(source_file,'(unknown file)'), COUNT(*), MIN(posted_at), MAX(posted_at), MAX(imported_at),
            GROUP_CONCAT(DISTINCT bank)
     FROM transactions
     GROUP BY COALESCE(source_file,'(unknown file)')
     ORDER BY MAX(imported_at) DESC`,
    { returnValue: 'resultRows' },
  ) as any[][];
  return r.map(row => ({
    sourceFile: row[0],
    count: row[1],
    firstPosted: row[2],
    lastPosted: row[3],
    importedAt: row[4],
    banks: String(row[5] || '').split(',').filter(Boolean),
  }));
}

/** Delete every transaction row belonging to an uploaded file. Returns rows removed. */
export async function removeUpload(sourceFile: string): Promise<number> {
  const db = await getDb();
  const before = db.exec('SELECT COUNT(*) FROM transactions', { returnValue: 'resultRows' }) as any[][];
  db.exec({ sql: 'DELETE FROM transactions WHERE source_file IS ?', bind: [sourceFile] });
  const after = db.exec('SELECT COUNT(*) FROM transactions', { returnValue: 'resultRows' }) as any[][];
  return Number(before[0][0]) - Number(after[0][0]);
}

/* ---------- factory seed ---------- */
export async function seedFactoryBudget() {
  await ensureMonth(SEED_MONTH, 'March 2026');
  await addExpensesBulk(
    FACTORY_SEED.map(s => ({ ...s, ym: SEED_MONTH, notes: s.notes, source: 'factory' })),
  );
  await metaSet('income', '20000.00');
  await metaSet('currentMonth', SEED_MONTH);
}
